import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { logisticsService } from '../services/api';
import api from '../services/api';

// ── Styles inline para PWA mobile ────────────────────────────────────
const S = {
  page: { minHeight: '100vh', background: '#f0f4ff', fontFamily: 'Inter, sans-serif' },
  header: { background: '#1e40af', color: '#fff', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerTitle: { fontSize: '1.1rem', fontWeight: 700, margin: 0 },
  headerSub: { fontSize: '.75rem', opacity: .8, margin: 0 },
  body: { padding: '1rem', maxWidth: 480, margin: '0 auto' },
  card: { background: '#fff', borderRadius: 12, padding: '1.25rem', marginBottom: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,.08)' },
  cardTitle: { fontSize: '.85rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '.75rem' },
  btn: { width: '100%', padding: '.875rem', borderRadius: 10, border: 'none', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', marginBottom: '.5rem' },
  btnPrimary: { background: '#1e40af', color: '#fff' },
  btnSuccess: { background: '#16a34a', color: '#fff' },
  btnDanger: { background: '#dc2626', color: '#fff' },
  btnSecondary: { background: '#f1f5f9', color: '#374151' },
  badge: { display: 'inline-block', padding: '.2rem .6rem', borderRadius: 20, fontSize: '.75rem', fontWeight: 600 },
  input: { width: '100%', padding: '.75rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '1rem', boxSizing: 'border-box', marginBottom: '.75rem' },
  row: { display: 'flex', gap: '.5rem' },
  opCard: { border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '1rem', marginBottom: '.75rem', cursor: 'pointer' },
  opCardActive: { border: '1.5px solid #1e40af', background: '#eff6ff' },
};

const TYPE_LABELS = { entrega: '🚛 Entrega', coleta: '📦 Coleta', limpeza: '🧹 Limpeza' };
const STATUS_COLORS = { agendado: '#f59e0b', em_rota: '#3b82f6', concluido: '#16a34a', cancelado: '#ef4444' };

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// ── QR Scanner usando câmera ──────────────────────────────────────────
function QRScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const [manualCode, setManualCode] = useState('');
  const [useManual, setUseManual] = useState(false);

  useEffect(() => {
    if (useManual) return;
    let stream;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch {
        setUseManual(true);
      }
    })();
    return () => { if (stream) stream.getTracks().forEach(t => t.stop()); };
  }, [useManual]);

  // Scan loop using BarcodeDetector API
  useEffect(() => {
    if (useManual || !('BarcodeDetector' in window)) { setUseManual(true); return; }
    const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
    let animId;
    const scan = async () => {
      if (videoRef.current?.readyState === 4) {
        try {
          const barcodes = await detector.detect(videoRef.current);
          if (barcodes.length > 0) {
            const raw = barcodes[0].rawValue;
            // Parse SANITECH|ASSET|{id}|{code}
            const parts = raw.split('|');
            if (parts[0] === 'SANITECH' && parts[1] === 'ASSET') {
              onScan(parts[3]); // asset code
              return;
            }
          }
        } catch {}
      }
      animId = requestAnimationFrame(scan);
    };
    animId = requestAnimationFrame(scan);
    return () => cancelAnimationFrame(animId);
  }, [useManual, onScan]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 100, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#1e40af', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#fff', fontWeight: 700 }}>📷 Escanear QR Code</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
      </div>

      {!useManual ? (
        <div style={{ flex: 1, position: 'relative' }}>
          <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 220, height: 220, border: '3px solid #fff', borderRadius: 12, boxShadow: '0 0 0 2000px rgba(0,0,0,.5)' }} />
          </div>
          <div style={{ position: 'absolute', bottom: '2rem', left: 0, right: 0, textAlign: 'center' }}>
            <button onClick={() => setUseManual(true)} style={{ background: 'rgba(255,255,255,.2)', border: 'none', color: '#fff', padding: '.5rem 1rem', borderRadius: 20, cursor: 'pointer' }}>
              Digitar código manualmente
            </button>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
          <p style={{ color: '#fff', textAlign: 'center', marginBottom: '1.5rem' }}>Digite o código do ativo:</p>
          <input
            style={{ ...S.input, textAlign: 'center', fontSize: '1.2rem', letterSpacing: '.1em', textTransform: 'uppercase' }}
            placeholder="Ex: BQ-001"
            value={manualCode}
            onChange={e => setManualCode(e.target.value.toUpperCase())}
            autoFocus
          />
          <button
            style={{ ...S.btn, ...S.btnPrimary, marginTop: '.5rem' }}
            onClick={() => manualCode && onScan(manualCode)}
          >
            Confirmar
          </button>
        </div>
      )}
    </div>
  );
}

// ── Operation card ────────────────────────────────────────────────────
function OperationCard({ op, onCheckin, onCheckout }) {
  const hasCheckin = !!op.checkin_at;
  const hasCheckout = !!op.checkout_at;

  return (
    <div style={S.opCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.5rem' }}>
        <span style={{ fontWeight: 700, fontSize: '1rem' }}>{TYPE_LABELS[op.type] || op.type}</span>
        <span style={{ ...S.badge, background: STATUS_COLORS[op.status] + '20', color: STATUS_COLORS[op.status] }}>{op.status}</span>
      </div>
      {op.address && <p style={{ fontSize: '.85rem', color: '#6b7280', margin: '0 0 .5rem' }}>📍 {op.address}</p>}
      <p style={{ fontSize: '.8rem', color: '#9ca3af', margin: '0 0 .75rem' }}>📅 {fmtDate(op.scheduled_date)}</p>

      {hasCheckin && <p style={{ fontSize: '.8rem', color: '#16a34a', margin: '0 0 .5rem' }}>✅ Check-in: {fmtDate(op.checkin_at)}</p>}
      {hasCheckout && <p style={{ fontSize: '.8rem', color: '#6366f1', margin: '0 0 .5rem' }}>🏁 Check-out: {fmtDate(op.checkout_at)}</p>}

      {!hasCheckin && (
        <button style={{ ...S.btn, ...S.btnSuccess, padding: '.625rem' }} onClick={() => onCheckin(op.id)}>
          ✅ Fazer Check-in
        </button>
      )}
      {hasCheckin && !hasCheckout && (
        <button style={{ ...S.btn, ...S.btnDanger, padding: '.625rem' }} onClick={() => onCheckout(op.id)}>
          🏁 Fazer Check-out
        </button>
      )}
    </div>
  );
}

// ── Main Driver App ───────────────────────────────────────────────────
export default function DriverApp() {
  const { user, logout } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [asset, setAsset] = useState(null);
  const [operations, setOperations] = useState([]);
  const [myOps, setMyOps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('scan'); // 'scan' | 'myops'

  // Load today's operations for this driver
  useEffect(() => {
    if (tab === 'myops') loadMyOps();
  }, [tab]);

  const loadMyOps = async () => {
    setLoading(true);
    try {
      const r = await logisticsService.list({ limit: 50, driver_id: user?.id });
      const ops = Array.isArray(r) ? r : r.data ?? [];
      // Filter today and pending
      const today = new Date().toDateString();
      setMyOps(ops.filter(op =>
        ['agendado', 'em_rota'].includes(op.status) &&
        op.scheduled_date &&
        new Date(op.scheduled_date).toDateString() === today
      ));
    } catch { toast.error('Erro ao carregar operações'); }
    finally { setLoading(false); }
  };

  const handleScan = async (code) => {
    setScanning(false);
    setLoading(true);
    try {
      const r = await api.get(`/logistics/qrscan/${code}`);
      setAsset(r.data.asset);
      setOperations(r.data.pending_operations);
      setTab('scan');
      if (r.data.pending_operations.length === 0) {
        toast('Nenhuma operação pendente para este ativo.', { icon: 'ℹ️' });
      } else {
        toast.success(`${r.data.pending_operations.length} operação(ões) encontrada(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ativo não encontrado');
    } finally { setLoading(false); }
  };

  const handleCheckin = async (opId) => {
    try {
      await logisticsService.checkin(opId, {});
      toast.success('Check-in registrado!');
      if (asset) handleScan(asset.code);
    } catch { toast.error('Erro ao fazer check-in'); }
  };

  const handleCheckout = async (opId) => {
    try {
      await logisticsService.checkout(opId, {});
      toast.success('Check-out registrado!');
      if (asset) handleScan(asset.code);
    } catch { toast.error('Erro ao fazer check-out'); }
  };

  return (
    <div style={S.page}>
      {scanning && <QRScanner onScan={handleScan} onClose={() => setScanning(false)} />}

      <div style={S.header}>
        <div>
          <p style={S.headerTitle}>🚽 SaniTech</p>
          <p style={S.headerSub}>Olá, {user?.name?.split(' ')[0]}!</p>
        </div>
        <button onClick={logout} style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', padding: '.4rem .8rem', borderRadius: 8, cursor: 'pointer', fontSize: '.85rem' }}>
          Sair
        </button>
      </div>

      {/* Tabs */}
      <div style={{ background: '#fff', display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
        {[['scan', '📷 Scanner'], ['myops', '📋 Minhas Ops']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            flex: 1, padding: '.875rem', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: tab === key ? 700 : 400,
            color: tab === key ? '#1e40af' : '#6b7280',
            borderBottom: tab === key ? '2px solid #1e40af' : '2px solid transparent',
            fontSize: '.9rem',
          }}>{label}</button>
        ))}
      </div>

      <div style={S.body}>
        {tab === 'scan' && (
          <>
            <div style={S.card}>
              <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '1rem', fontSize: '.9rem' }}>
                Escaneie o QR Code do banheiro para ver as operações pendentes
              </p>
              <button style={{ ...S.btn, ...S.btnPrimary }} onClick={() => setScanning(true)}>
                📷 Escanear QR Code
              </button>
            </div>

            {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Carregando...</div>}

            {asset && !loading && (
              <>
                <div style={{ ...S.card, borderLeft: '4px solid #1e40af' }}>
                  <p style={S.cardTitle}>Ativo Identificado</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem', fontSize: '.9rem' }}>
                    <div><span style={{ color: '#9ca3af' }}>Código</span><br /><strong>{asset.code}</strong></div>
                    <div><span style={{ color: '#9ca3af' }}>Tipo</span><br /><strong>{asset.type}</strong></div>
                    <div><span style={{ color: '#9ca3af' }}>Status</span><br /><strong>{asset.status}</strong></div>
                    <div><span style={{ color: '#9ca3af' }}>Local</span><br /><strong style={{ fontSize: '.8rem' }}>{asset.location || '—'}</strong></div>
                  </div>
                </div>

                {operations.length > 0 ? (
                  <div style={S.card}>
                    <p style={S.cardTitle}>Operações Pendentes ({operations.length})</p>
                    {operations.map(op => (
                      <OperationCard key={op.id} op={op} onCheckin={handleCheckin} onCheckout={handleCheckout} />
                    ))}
                  </div>
                ) : (
                  <div style={{ ...S.card, textAlign: 'center', color: '#6b7280' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '.5rem' }}>✅</div>
                    <p>Nenhuma operação pendente para este ativo.</p>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {tab === 'myops' && (
          <div style={S.card}>
            <p style={S.cardTitle}>Operações de Hoje</p>
            {loading && <p style={{ color: '#6b7280', textAlign: 'center' }}>Carregando...</p>}
            {!loading && myOps.length === 0 && (
              <div style={{ textAlign: 'center', color: '#6b7280', padding: '1rem' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '.5rem' }}>📋</div>
                <p>Nenhuma operação agendada para hoje.</p>
              </div>
            )}
            {myOps.map(op => (
              <OperationCard key={op.id} op={op} onCheckin={handleCheckin} onCheckout={handleCheckout} />
            ))}
            <button style={{ ...S.btn, ...S.btnSecondary, marginTop: '.5rem' }} onClick={loadMyOps}>
              🔄 Atualizar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
